import { api } from '@/lib/axios'
import type { ExportRequest } from '@/lib/export-request'
import type {
  AssignmentCandidates,
  AssignmentConflict,
  AutoArrangeMode,
  BulkAssignResult,
  CockpitBoard,
  CockpitCard,
  CockpitGroup,
  CockpitLayoutSnapshot,
  CockpitPhase,
  CockpitSettings,
  CockpitWeek,
  StepAssignment,
} from '../types/cockpit.types'

/**
 * AP-35 Phase D2 — Aufrufe der Arbeitsschritt-Cockpits und der Einteilung
 * (Detailblatt 11 9.). Filter gehen als Query, wie die Liste sie führt.
 */

export type CockpitFilter = Record<string, string | number | boolean | undefined>

const ohneLeere = (p: CockpitFilter) =>
  Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== ''))

export async function fetchCockpits(): Promise<{ phases: CockpitPhase[]; settings: CockpitSettings }> {
  const res = await api.get<{ data: { phases: CockpitPhase[]; settings: CockpitSettings } }>('/work-cockpit')
  return res.data.data
}

export async function fetchBoard(phaseKey: string, filter: CockpitFilter): Promise<CockpitBoard> {
  const res = await api.get<{ data: CockpitBoard }>(`/work-cockpit/${phaseKey}/cards`, { params: ohneLeere(filter) })
  return res.data.data
}

export async function fetchCard(stepId: string): Promise<CockpitCard> {
  const res = await api.get<{ data: CockpitCard }>(`/work-cockpit/step/${stepId}`)
  return res.data.data
}

export async function placeCard(phaseKey: string, stepId: string, lage: { x?: number | null; y?: number | null; groupId?: string | null }) {
  await api.put(`/work-cockpit/${phaseKey}/cards/${stepId}/position`, lage)
}

export async function placeCards(phaseKey: string, payload: { stepIds: string[]; groupId?: string | null; dx?: number; dy?: number }) {
  await api.put(`/work-cockpit/${phaseKey}/cards/position`, payload)
}

export async function createCockpitGroup(phaseKey: string, payload: Partial<CockpitGroup> & { name: string; stepIds?: string[] }): Promise<CockpitGroup> {
  const res = await api.post<{ data: CockpitGroup }>(`/work-cockpit/${phaseKey}/groups`, payload)
  return res.data.data
}

export async function updateCockpitGroup(phaseKey: string, id: string, payload: Partial<CockpitGroup>): Promise<CockpitGroup> {
  const res = await api.patch<{ data: CockpitGroup }>(`/work-cockpit/${phaseKey}/groups/${id}`, payload)
  return res.data.data
}

export async function deleteCockpitGroup(phaseKey: string, id: string) {
  await api.delete(`/work-cockpit/${phaseKey}/groups/${id}`)
}

export async function autoArrange(phaseKey: string, by: AutoArrangeMode, replaceGroups: boolean, filter: CockpitFilter) {
  const res = await api.post<{ data: { previous: CockpitLayoutSnapshot; groups: number } }>(`/work-cockpit/${phaseKey}/auto-arrange`, {
    ...ohneLeere(filter),
    by,
    replaceGroups,
  })
  return res.data.data
}

export async function restoreLayout(phaseKey: string, stand: CockpitLayoutSnapshot) {
  await api.put(`/work-cockpit/${phaseKey}/layout`, {
    groups: stand.groups.map(({ id, name, color, x, y, w, h, collapsed, sortOrder }) => ({ id, name, color, x, y, w, h, collapsed, sortOrder })),
    cards: stand.cards,
  })
}

export async function fetchLayoutOwners(phaseKey: string): Promise<{ userId: string; name: string; cards: number }[]> {
  const res = await api.get<{ data: { userId: string; name: string; cards: number }[] }>(`/work-cockpit/${phaseKey}/layout/owners`)
  return res.data.data
}

export async function copyLayout(phaseKey: string, fromUserId: string) {
  const res = await api.post<{ data: { previous: CockpitLayoutSnapshot } }>(`/work-cockpit/${phaseKey}/layout/copy`, { fromUserId })
  return res.data.data
}

export async function fetchCockpitList(
  phaseKey: string,
  params: CockpitFilter,
): Promise<{ data: CockpitCard[]; meta: { total: number; page: number; limit: number; totalPages: number } }> {
  const res = await api.get(`/work-cockpit/${phaseKey}/list`, { params: ohneLeere(params) })
  return res.data
}

export function cockpitListExportRequest(
  phaseKey: string,
  params: CockpitFilter,
  columns: { key: string; label: string }[],
): ExportRequest {
  return {
    url: `/work-cockpit/${phaseKey}/list/export`,
    params: { ...ohneLeere(params), columns: JSON.stringify(columns), locale: navigator.language },
  }
}

export async function fetchWeek(params: CockpitFilter): Promise<CockpitWeek> {
  const res = await api.get<{ data: CockpitWeek }>('/work-cockpit/week', { params: ohneLeere(params) })
  return res.data.data
}

export function weekExportRequest(params: CockpitFilter & { format: string; orientation?: string }): ExportRequest {
  return { url: '/work-cockpit/week/export', params: { ...ohneLeere(params), locale: navigator.language } }
}

// ── Einteilung ──────────────────────────────────────────────────────────────

export async function fetchCandidates(stepIds: string[], from?: string, to?: string): Promise<AssignmentCandidates> {
  const res = await api.get<{ data: AssignmentCandidates }>('/projects/assignments/candidates', {
    params: ohneLeere({ stepIds: stepIds.join(','), from, to }),
  })
  return res.data.data
}

export interface BulkAssignPayload {
  stepIds: string[]
  users: { userId: string; plannedHours?: number | null; isLead?: boolean }[]
  startsOn?: string
  endsOn?: string
  note?: string
}

export async function bulkAssign(payload: BulkAssignPayload, dryRun: boolean): Promise<BulkAssignResult> {
  const res = await api.post<{ data: BulkAssignResult }>('/projects/assignments/bulk', payload, {
    params: dryRun ? { dryRun: 1 } : undefined,
  })
  return res.data.data
}

export async function fetchStepAssignments(stepId: string): Promise<StepAssignment[]> {
  const res = await api.get<{ data: StepAssignment[] }>(`/projects/work-steps/${stepId}/assignments`)
  return res.data.data
}

export async function updateAssignment(
  id: string,
  payload: Partial<{ startsOn: string; endsOn: string; userId: string; plannedHours: number | null; note: string | null; isLead: boolean }>,
  dryRun = false,
): Promise<{ conflicts: AssignmentConflict[]; assignment: StepAssignment | null }> {
  const res = await api.patch(`/projects/assignments/${id}`, payload, { params: dryRun ? { dryRun: 1 } : undefined })
  return res.data.data
}

export async function endAssignment(id: string) {
  await api.post(`/projects/assignments/${id}/end`)
}

export async function deleteAssignment(id: string) {
  await api.delete(`/projects/assignments/${id}`)
}
